import { Injectable, OnDestroy, effect, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';

import {
  INACTIVITY_TIMEOUT_RETURN_URL_KEY,
  INACTIVITY_TIMEOUT_ROUTE,
} from '../../../../features/quoter/payment-routes.constants';
import { QuoterStateService } from '../../../../features/quoter/services/quoter-state.service';
import { LoadingService } from '../loader/loading.service';
import { StorageService } from '../storage/storage.service';

@Injectable({ providedIn: 'root' })
export class IdleTimeoutService implements OnDestroy {
  private static readonly INACTIVITY_TIMEOUT_MS = 3 * 60 * 1000;
  private static readonly ACTIVITY_EVENTS: readonly string[] = [
    'mousemove',
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
    'click',
  ];

  private readonly router = inject(Router);
  private readonly quoterState = inject(QuoterStateService);
  private readonly storageService = inject(StorageService);
  private readonly loadingService = inject(LoadingService);

  private timeoutRef: ReturnType<typeof setTimeout> | null = null;
  private routerSubscription: Subscription | null = null;
  private initialized = false;

  private readonly onUserActivity = () => {
    if (!this.initialized || !this.shouldTrack(this.router.url)) {
      return;
    }

    if (this.loadingService.hasPendingRequests()) {
      return;
    }

    this.restartTimer();
  };

  constructor() {
    effect(() => {
      const hasPendingRequests = this.loadingService.hasPendingRequests();
      if (!this.initialized) {
        return;
      }

      if (hasPendingRequests) {
        this.clearTimer();
      } else {
        this.resumeTrackingIfApplicable();
      }
    });
  }

  init(): void {
    if (this.initialized || typeof window === 'undefined') {
      return;
    }

    this.initialized = true;

    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navigation = event as NavigationEnd;
        this.handleRouteChange(navigation.urlAfterRedirects || navigation.url);
      });

    for (const eventName of IdleTimeoutService.ACTIVITY_EVENTS) {
      window.addEventListener(eventName, this.onUserActivity, { passive: true });
    }

    this.handleRouteChange(this.router.url);
  }

  stop(): void {
    if (!this.initialized || typeof window === 'undefined') {
      return;
    }

    this.initialized = false;
    this.clearTimer();

    this.routerSubscription?.unsubscribe();
    this.routerSubscription = null;

    for (const eventName of IdleTimeoutService.ACTIVITY_EVENTS) {
      window.removeEventListener(eventName, this.onUserActivity);
    }
  }

  resumeTrackingIfApplicable(): void {
    if (!this.initialized || !this.shouldTrack(this.router.url)) {
      return;
    }

    if (!this.loadingService.hasPendingRequests()) {
      this.restartTimer();
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private handleRouteChange(url: string): void {
    if (!this.initialized) {
      return;
    }

    this.clearTimer();
    if (this.shouldTrack(url) && !this.loadingService.hasPendingRequests()) {
      this.restartTimer();
    }
  }

  private shouldTrack(url: string): boolean {
    return !this.isExcludedRoute(url);
  }

  private isExcludedRoute(url: string): boolean {
    // Excluir TODAS las rutas de terminal-flows (tanto directas como bajo /cotizador/)
    const terminalFlowRoutes = [
      '/terminal-flows/',
      '/cotizador/rechazo',
      '/cotizador/no-colombiano',
      '/cotizador/pep',
      '/cotizador/no-vacunacion',
      '/cotizador/sarlaft-required-action',
      '/cotizador/procesando_pago',
      '/error',
      '/error-404',
      '/error-500',
      '/cumulo',
      '/ciudad-inaccesible',
      '/consultable-riesgos',
    ];

    return (
      url.includes(INACTIVITY_TIMEOUT_ROUTE) ||
      terminalFlowRoutes.some(route => url.includes(route))
    );
  }

  private restartTimer(): void {
    this.clearTimer();

    this.timeoutRef = setTimeout(() => {
      this.triggerTimeout();
    }, IdleTimeoutService.INACTIVITY_TIMEOUT_MS);
  }

  private clearTimer(): void {
    if (this.timeoutRef) {
      clearTimeout(this.timeoutRef);
      this.timeoutRef = null;
    }
  }

  private triggerTimeout(): void {
    if (!this.initialized || !this.shouldTrack(this.router.url) || this.loadingService.hasPendingRequests()) {
      return;
    }

    this.loadingService.forceHideAll();
    this.quoterState.saveStateSnapshot();
    this.storageService.setSessionItem(INACTIVITY_TIMEOUT_RETURN_URL_KEY, this.router.url);
    // Usar replaceUrl para reemplazar la entrada en el historial
    // Esto previene que el usuario pueda volver a inactividad-timeout usando el back del navegador
    void this.router.navigate([INACTIVITY_TIMEOUT_ROUTE], { replaceUrl: true });
  }
}
